<script setup>
import WaveTop from '@/icons/wave-top.svg';
import WaveTopSmall from '@/icons/wave-top-small.svg';
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs.vue';

const props = defineProps({
    data: { type: Object, default: () => {} },

    color: {
        type: String,
        default: '',
    },

    titleColor: {
        type: String,
        default: '',
    },

    suptitle: {
        type: String,
        default: '',
    },

    subtitle: {
        type: String,
        default: '',
    },

    title: {
        type: String,
        default: '',
    },

    typePancarte: {
        type: String,
        default: 'halte',
    },

    typePersonnage: {
        type: String,
        default: 'svg',
    },

    personnageImage: {
        type: Object,
        default: () => {},
    },

    crumbs: {
        type: Array,
        default: () => [],
    },
});

function formatSubtitle(text) {
    return text.replace(/1er/g, '1<sup>er</sup>');
}
</script>

<template>
    <div class="Header text-left md:text-center">
        <!-- Tailwind: group-hover:shadow-dark-blue -->
        <div
            :class="['text-' + color]"
            class="Waves absolute left-0 top-0 z-0 w-full overflow-x-hidden print:hidden"
        >
            <WaveTop
                class="max-w-screen mt-[-80px] hidden h-auto w-[1920px] fill-current md:block xl:mt-0 3xl:w-full"
                :class="['text-' + color]"
            />
            <WaveTopSmall
                class="block h-auto w-full fill-current md:hidden"
                :class="['text-' + color]"
            />
        </div>
        <div class="relative flex items-start justify-between px-6 print:hidden md:mb-5 xl:px-20">
            <div class="max-w-1/2 md:w-[348px] md:max-w-full xl:w-[439px] 3xl:w-[24.4vw]">
                <template v-if="typePancarte === 'halte'">
                    <svg
                        width="341"
                        height="227"
                        viewBox="0 0 341 227"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="mt-5 h-auto w-full md:mt-[68px] md:max-w-full xl:mt-[100px] 2xl:mt-[88px] 3xl:mt-[calc(10.9vw-88px)]"
                    >
                        <rect
                            width="341"
                            height="171"
                            rx="12.7748"
                            fill="#123249"
                        />
                        <text
                            fill="white"
                            xml:space="preserve"
                            style="white-space: pre"
                            font-size="43.43"
                            font-weight="bold"
                            letter-spacing="0em"
                        >
                            <tspan
                                x="112.046"
                                y="59.024"
                            >
                                {{ data.suptitle }}
                            </tspan>
                        </text>
                        <text
                            fill="white"
                            xml:space="preserve"
                            style="white-space: pre"
                            font-size="57.01"
                            font-weight="bold"
                            letter-spacing="-0.01em"
                        >
                            <tspan
                                y="103.279"
                                x="50%"
                                text-anchor="middle"
                            >
                                {{ data.subtitle }}
                            </tspan>
                        </text>
                        <router-link
                            class="group"
                            :to="{ name: 'home.' + lang }"
                        >
                            <path
                                d="M288.466 217.809L293.87 160.845C294.327 156.035 291.854 151.875 288.339 151.542L92.8177 132.993C91.3071 132.849 89.7705 133.457 88.492 134.685L62.9289 161.007C59.638 164.199 59.0433 170.468 61.6736 174.239L81.8325 204.881C82.8557 206.345 84.2522 207.213 85.7629 207.357L281.284 225.906C284.799 226.239 288.01 222.619 288.466 217.809Z"
                                fill="#33567B"
                                class="fill-current transition duration-500 group-hover:translate-y-[-3px] group-hover:drop-shadow-lg group-active:translate-y-0 group-active:drop-shadow-none group-active:duration-0"
                                :class="['text-' + color, 'group-hover:shadow-' + titleColor]"
                            />
                            <g class="transition duration-500 group-hover:translate-y-[-3px] group-active:translate-y-0 group-active:duration-0">
                                <text
                                    transform="translate(96.6055 142.393) rotate(5.41942)"
                                    fill="white"
                                    xml:space="preserve"
                                    style="white-space: pre"
                                    font-size="23.9995"
                                    font-weight="bold"
                                    letter-spacing="0em"
                                >
                                    <tspan
                                        x="26.0893"
                                        y="23.0391"
                                    >
                                        {{ data.supsubtitle }}
                                    </tspan>
                                </text>
                                <text
                                    transform="translate(119.794 162.673) rotate(5.41942)"
                                    fill="white"
                                    xml:space="preserve"
                                    style="white-space: pre"
                                    font-size="28.4995"
                                    font-weight="bold"
                                    letter-spacing="-0.01em"
                                >
                                    <tspan
                                        x="11.3873"
                                        y="26.8"
                                    >
                                        {{ data.subsubtitle }}
                                    </tspan>
                                </text>
                            </g>
                        </router-link>
                    </svg>
                </template>
                <template v-else>
                    <svg
                        overflow="visible"
                        width="439"
                        height="234"
                        viewBox="0 0 439 234"
                        xmlns="http://www.w3.org/2000/svg"
                        class="mt-5 h-auto w-full md:mt-[100px] md:max-w-full xl:mt-[116px] 2xl:mt-[104px] 3xl:mt-[calc(10.9vw-104px)]"
                    >
                        <path
                            d="M0.000183105 155.562V15.8151C0.000183105 7.071 5.1662 0 11.5557 0H366.987C369.733 0 372.398 1.35738 374.491 3.78804L434.145 73.6773C439.528 79.9907 439.528 91.3864 434.145 97.7314L374.491 167.589C372.398 170.051 369.733 171.377 366.987 171.377H11.5557C5.1662 171.377 0.000183105 164.306 0.000183105 155.562Z"
                            class="fill-current"
                            :class="['text-' + titleColor]"
                        />

                        <text
                            x="50%"
                            y="74"
                            font-weight="bold"
                            font-size="43px"
                            text-anchor="middle"
                            transform="translate(-9 0)"
                            class="fill-current text-white"
                        >
                            {{ data.suptitle }}
                        </text>
                        <text
                            x="50%"
                            y="116"
                            font-weight="bold"
                            font-size="56px"
                            text-anchor="middle"
                            transform="translate(-9 0)"
                            class="fill-current text-white"
                        >
                            {{ data.subtitle }}
                        </text>

                        <router-link
                            v-if="data.supsubtitle && data.subsubtitle"
                            class="group"
                            :to="{ name: 'home.' + lang }"
                        >
                            <path
                                d="M307.917 224.589L313.322 167.624C313.778 162.813 311.305 158.654 307.79 158.321L112.265 139.771C110.754 139.628 109.218 140.235 107.939 141.463L82.3757 167.786C79.0847 170.978 78.4899 177.247 81.1203 181.018L101.28 211.661C102.303 213.125 103.699 213.993 105.21 214.136L300.735 232.686C304.25 233.019 307.461 229.399 307.917 224.589Z"
                                class="fill-current transition duration-500 group-hover:translate-y-[-3px] group-hover:drop-shadow-lg group-active:translate-y-0 group-active:drop-shadow-none group-active:duration-0"
                                :class="['text-' + color, 'group-hover:shadow-' + titleColor]"
                            />

                            <g class="transition duration-500 group-hover:translate-y-[-3px] group-active:translate-y-0 group-active:duration-0">
                                <text
                                    x="50%"
                                    y="167"
                                    font-weight="bold"
                                    font-size="24px"
                                    text-anchor="middle"
                                    transform="rotate(5 44.2515 109.463) translate(-17 0)"
                                    class="fill-current text-white"
                                >
                                    {{ data.supsubtitle }}
                                </text>
                                <text
                                    x="50%"
                                    y="190"
                                    font-weight="bold"
                                    font-size="24px"
                                    text-anchor="middle"
                                    transform="rotate(5 44.2515 109.463) translate(-17 0)"
                                    class="fill-current text-white"
                                >
                                    {{ data.subsubtitle }}
                                </text>
                            </g>
                        </router-link>

                        <g v-else>
                            <rect
                                x="67.436"
                                y="155.873"
                                width="271.455"
                                height="81.4366"
                                rx="10.8582"
                                class="rotate-[-3deg] fill-current transition duration-500"
                                :class="['text-' + color]"
                            />
                            <text
                                x="50%"
                                y="213"
                                font-size="48.8px"
                                text-anchor="middle"
                                font-weight="bold"
                                class="translate-x-[-14px] rotate-[-3deg] fill-current text-white"
                            >
                                {{ data.supsubtitle }}
                            </text>
                        </g>
                    </svg>
                </template>
            </div>
            <template v-if="typePersonnage == 'svg' && data.personnageEntete">
                <div
                    class="relative mt-[14px] w-[295px] max-w-4/10 md:max-w-full 3xl:mt-[calc(3.4vw-14px)] 3xl:w-[16.4vw]"
                    v-html="data.personnageEntete.inline"
                />
            </template>
            <template v-else-if="typePersonnage == 'img' && personnageImage">
                <div class="relative mt-[14px] w-[295px] max-w-4/10 md:max-w-full 3xl:mt-[calc(3.4vw-14px)] 3xl:w-[16.4vw]">
                    <img
                        class="mx-auto max-h-[175px] md:max-h-[350px]"
                        :src="personnageImage[0].url"
                    />
                </div>
            </template>
        </div>
        <div
            class="w-full px-6 xl:px-20"
            :class="['text-' + titleColor]"
        >
            <div
                v-if="suptitle !== ''"
                class="Header__suptitle font-light print:hidden md:font-medium"
            >
                <span class="Recipe__category">{{ suptitle }}</span>
            </div>
            <h1
                class="Header__title h2 mb-2 text-balance print:text-dark-gray"
                :class="['text-' + titleColor]"
                v-html="title !== '' ? title : data.title"
            />
            <div
                v-if="subtitle !== ''"
                class="Header__subtitle font-light print:hidden md:font-medium"
            >
                <span
                    class="Recipe__category"
                    v-html="formatSubtitle(subtitle)"
                ></span>
            </div>
            <div class="Header__breadcrumbs mb-6 flex text-xs print:hidden md:justify-center lg:text-base">
                <Breadcrumbs
                    :entry="data.sectionHandle"
                    :crumbs="crumbs"
                />
            </div>
        </div>
    </div>
</template>
